import { SHOW_FILTERS } from "./manifestShowFilters";
import { omnisearchHasCorrectionDateTerm } from "./mergeDefaultAndOmnisearch";

/**
 * The merged list's hidden page scope: the tenant, plus the year picker's
 * shipped-year bound.
 *
 * The year is a *default* scope rather than a pinned filter, the same way V2's
 * Manifests screen treats it: when the active search carries a
 * correction/raised-date term (the vendor dashboard's chart drilldowns), the
 * year is dropped. Those charts scope by request/raised date, so keeping the
 * shipped-year bound would silently drop cross-year rows the chart counted —
 * and surfacing a shipped-date override in the visible input is noise. The date
 * term takes over as the grid's time scope instead.
 *
 * The needs-review preset is dropped from the year for the same reason. When a
 * manifest is reviewed has nothing to do with when it shipped: a document
 * uploaded today can be last year's paperwork, and scoping the queue of work to
 * one shipped-year hides exactly the backlog the preset exists to surface — and
 * miscounts it in the Show picker, which shares this scope.
 *
 * `shipDateKey` is a parameter because the two consumers speak different
 * vocabularies for the same date. The `shippingPaperwork` query registers
 * `shipDate` (a column both kinds carry, which is why the year scopes bills of
 * lading too); the Show picker's `processedManifestShowFilterCounts` query is
 * the manifest-only one and registers `shippedDate`.
 */
export function manifestsV3DefaultSearch({
  tenantId,
  shippedDateYear,
  omnisearch,
  shipDateKey,
}: {
  tenantId: string;
  shippedDateYear: number;
  omnisearch: string;
  shipDateKey: "shipDate" | "shippedDate";
}): string {
  if (omnisearchHasCorrectionDateTerm(omnisearch) || omnisearchAsksForReview(omnisearch)) {
    return `tenantId:${tenantId}`;
  }
  return `tenantId:${tenantId} ${shipDateKey}:${shippedDateYear}`;
}

/**
 * Whether the active search asks for review work. Matched on the preset's own
 * terms rather than a copied literal, so the two cannot drift apart.
 *
 * The Document Review column filters on the same key, so this is true whether
 * the token came from the Show preset or from the column. That is deliberate —
 * the reason to drop the year scope is that a document uploaded today can be
 * last year's paperwork, which holds however the filter was set — but it does
 * mean the year picker stops applying without having been touched, so the
 * chrome reads this too and says so.
 */
export function omnisearchAsksForReview(omnisearch: string): boolean {
  const tokens = omnisearch.split(/\s+/).filter((token) => token.length > 0);
  return SHOW_FILTERS.needsDocumentReview.terms.every((term) => tokens.includes(term));
}
